import React, { useState, useEffect } from 'react';
import { Globe, Plus, CheckCircle, XCircle, RefreshCw, Copy, Settings, CreditCard, Search, RotateCcw, ExternalLink, AlertTriangle } from 'lucide-react';

const CustomDomains = () => {
  const [activeTab, setActiveTab] = useState('domains');
  const [domains, setDomains] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState('ALL');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [purchaseActionLoading, setPurchaseActionLoading] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showPurchaseDetailsModal, setShowPurchaseDetailsModal] = useState(false);

  useEffect(() => {
    fetchDomains();
    fetchPurchases();
    fetchStores();
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, [purchaseStatus]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/custom-domains', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains || []);
      } else {
        throw new Error('Failed to fetch domains');
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Failed to load custom domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await fetch('/api/admin/stores', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStores(data.stores || []);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchPurchases = async () => {
    try {
      setPurchasesLoading(true);
      const params = new URLSearchParams();
      if (purchaseStatus && purchaseStatus !== 'ALL') params.set('status', purchaseStatus);
      if (purchaseSearch.trim()) params.set('q', purchaseSearch.trim());

      const response = await fetch(`/api/admin/domain-purchases${params.toString() ? `?${params.toString()}` : ''}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
      } else {
        throw new Error('Failed to fetch domain purchases');
      }
    } catch (err) {
      console.error('Error fetching domain purchases:', err);
      setError('Failed to load domain purchases');
    } finally {
      setPurchasesLoading(false);
    }
  };

  const handleVerifyDomainPurchasePayment = async (purchaseId) => {
    try {
      setPurchaseActionLoading(`verify-${purchaseId}`);
      setError('');
      setSuccess('');

      const response = await fetch(`/api/admin/domain-purchases/${purchaseId}/verify-payment`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Domain payment checked');
        fetchPurchases();
        fetchDomains();
      } else {
        setError(data.error || data.message || 'Failed to verify payment');
      }
    } catch (err) {
      console.error('Error verifying domain purchase payment:', err);
      setError('Failed to verify domain payment');
    } finally {
      setPurchaseActionLoading('');
    }
  };

  const handleRetryDomainProvisioning = async (purchaseId) => {
    try {
      setPurchaseActionLoading(`retry-${purchaseId}`);
      setError('');
      setSuccess('');

      const response = await fetch(`/api/admin/domain-purchases/${purchaseId}/retry-provisioning`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Domain provisioning checked');
        fetchPurchases();
        fetchDomains();
      } else {
        setError(data.error || data.message || 'Failed to retry provisioning');
      }
    } catch (err) {
      console.error('Error retrying domain provisioning:', err);
      setError('Failed to retry domain provisioning');
    } finally {
      setPurchaseActionLoading('');
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim() || !selectedStoreId) {
      setError('Please enter a domain and select a store');
      return;
    }

    try {
      setVerifying(true);
      setError('');
      setSuccess('');

      const response = await fetch('/api/admin/custom-domains', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          domain: newDomain.trim(),
          storeId: selectedStoreId
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Custom domain created successfully');
        setNewDomain('');
        setSelectedStoreId('');
        setShowAddModal(false);
        fetchDomains();
      } else {
        setError(data.error || 'Failed to create domain');
      }
    } catch (err) {
      console.error('Error creating domain:', err);
      setError('Failed to create domain');
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`/api/admin/custom-domains/${domainId}/verify`, {
        method: 'PUT',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Domain verification initiated');
        fetchDomains();
      } else {
        setError(data.error || 'Failed to verify domain');
      }
    } catch (err) {
      console.error('Error verifying domain:', err);
      setError('Failed to verify domain');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;

    try {
      setError('');
      setSuccess('');

      const response = await fetch(`/api/admin/custom-domains/${domainId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Domain deleted successfully');
        fetchDomains();
      } else {
        setError(data.error || 'Failed to delete domain');
      }
    } catch (err) {
      console.error('Error deleting domain:', err);
      setError('Failed to delete domain');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'VERIFIED':
      case 'ACTIVE':
        return <span className="badge badge-success">{status === 'ACTIVE' ? 'Active' : 'Verified'}</span>;
      case 'PENDING':
        return <span className="badge badge-warning">Pending</span>;
      case 'FAILED':
        return <span className="badge badge-error">Failed</span>;
      default:
        return <span className="badge badge-neutral">{status || 'Pending'}</span>;
    }
  };

  const getPurchaseStatusBadge = (status) => {
    switch (status) {
      case 'PROVISIONED':
        return <span className="badge badge-success">Provisioned</span>;
      case 'PROVISIONING':
        return <span className="badge badge-info">Provisioning</span>;
      case 'PAID':
        return <span className="badge badge-warning">Paid</span>;
      case 'PENDING_PAYMENT':
        return <span className="badge badge-neutral">Pending Payment</span>;
      case 'PROVISIONING_FAILED':
        return <span className="badge badge-error">Failed</span>;
      case 'CANCELLED':
      case 'REFUNDED':
        return <span className="badge badge-neutral">{status.replace('_', ' ')}</span>;
      default:
        return <span className="badge badge-neutral">{status || 'Unknown'}</span>;
    }
  };

  const formatMoney = (value, currency = 'NGN') => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number.isFinite(amount) ? amount : 0);
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString();
  };

  const canVerifyPayment = (purchase) => (
    purchase?.status === 'PENDING_PAYMENT' && purchase?.paystackReference
  );

  const canRetryProvisioning = (purchase) => (
    ['PAID', 'PROVISIONING_FAILED'].includes(purchase?.status)
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid var(--border)',
          borderTop: '3px solid var(--brand)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <div className="text-muted">Loading custom domains...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-xl">
        <div>
          <h1 className="admin-h1 mb-sm">Custom Domains</h1>
          <p className="text-muted mb-0">Manage custom domains for merchant stores</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          <Plus size={18} />
          Add Domain
        </button>
      </div>

      {error && (
        <div className="card mb-xl" style={{ background: 'var(--error-bg)', borderColor: 'var(--error-color)' }}>
          <div className="card-body flex gap-md items-center">
            <XCircle size={20} color="var(--error-color)" />
            <span style={{ color: 'var(--error-color)' }}>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="card mb-xl" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-color)' }}>
          <div className="card-body flex gap-md items-center">
            <CheckCircle size={20} color="var(--success-color)" />
            <span style={{ color: 'var(--success-color)' }}>{success}</span>
          </div>
        </div>
      )}

      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        <button
          className={`btn ${activeTab === 'domains' ? 'btn-primary' : ''}`}
          onClick={() => setActiveTab('domains')}
        >
          <Globe size={16} />
          Connected Domains
          <span className="badge badge-neutral">{domains.length}</span>
        </button>
        <button
          className={`btn ${activeTab === 'purchases' ? 'btn-primary' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          <CreditCard size={16} />
          Purchased Domains
          <span className="badge badge-neutral">{purchases.length}</span>
        </button>
      </div>

      {activeTab === 'domains' ? (
        domains.length === 0 ? (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '4rem' }}>
              <Globe size={64} color="var(--text-muted)" style={{ margin: '0 auto 24px' }} />
              <h3 className="mb-sm">No Custom Domains</h3>
              <p className="text-muted mb-xl">No custom domains have been created yet.</p>
              <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                <Plus size={18} />
                Add Your First Domain
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2" style={{ gap: 'var(--space-xl)' }}>
            {domains.map(domain => (
              <div key={domain.id} className="card">
                <div className="card-body">
                  <div className="flex justify-between items-start mb-lg">
                    <div>
                      <h3 className="mb-xs font-bold">{domain.domain}</h3>
                      <p className="text-muted text-sm mb-0">
                        {domain.store?.name || 'No store assigned'}
                      </p>
                    </div>
                    {getStatusBadge(domain.status)}
                  </div>

                  <div className="grid grid-cols-2 gap-md mb-lg">
                    <div>
                      <div className="card-title">Store</div>
                      <div className="text-sm font-semibold">
                        {domain.store?.name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Merchant</div>
                      <div className="text-sm font-semibold">
                        {domain.store?.owner?.username || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Merchant ID</div>
                      <div className="text-sm font-semibold font-mono">
                        {domain.store?.owner?.id ? `${domain.store.owner.id.substring(0, 12)}...` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Tier</div>
                      <div className="text-sm font-semibold">
                        <span className={`badge ${domain.store?.owner?.merchantTier === 'PREMIUM' ? 'badge-success' : domain.store?.owner?.merchantTier === 'PRO' ? 'badge-info' : 'badge-neutral'}`}>
                          {domain.store?.owner?.merchantTier || 'FREE'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Created</div>
                      <div className="text-sm font-semibold">
                        {new Date(domain.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    {domain.status === 'VERIFIED' && domain.updatedAt && (
                      <div>
                        <div className="card-title">Verified</div>
                        <div className="text-sm font-semibold">
                          {new Date(domain.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-sm">
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        setSelectedDomain(domain);
                        setShowDetailsModal(true);
                      }}
                    >
                      <Settings size={16} />
                      Manage
                    </button>

                    {domain.status === 'PENDING' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleVerifyDomain(domain.id)}
                      >
                        <RefreshCw size={16} />
                        Verify
                      </button>
                    )}

                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteDomain(domain.id)}
                    >
                      <XCircle size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div>
          <div className="card mb-xl">
            <div className="card-body">
              <div className="flex justify-between items-center gap-md" style={{ flexWrap: 'wrap' }}>
                <div className="flex gap-sm items-center" style={{ flex: '1 1 360px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      className="input"
                      value={purchaseSearch}
                      onChange={(e) => setPurchaseSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') fetchPurchases();
                      }}
                      placeholder="Search domain, merchant, store, Paystack ref"
                      style={{ paddingLeft: 38 }}
                    />
                  </div>
                  <button className="btn" onClick={fetchPurchases}>
                    Search
                  </button>
                </div>
                <div className="flex gap-sm items-center">
                  <select
                    className="input"
                    value={purchaseStatus}
                    onChange={(e) => setPurchaseStatus(e.target.value)}
                    style={{ minWidth: 190 }}
                  >
                    <option value="ALL">All statuses</option>
                    <option value="PENDING_PAYMENT">Pending payment</option>
                    <option value="PAID">Paid</option>
                    <option value="PROVISIONING">Provisioning</option>
                    <option value="PROVISIONED">Provisioned</option>
                    <option value="PROVISIONING_FAILED">Failed</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="REFUNDED">Refunded</option>
                  </select>
                  <button className="btn" onClick={fetchPurchases} disabled={purchasesLoading}>
                    <RefreshCw size={16} style={purchasesLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {purchasesLoading ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
                <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                <div className="text-muted">Loading purchased domains...</div>
              </div>
            </div>
          ) : purchases.length === 0 ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '4rem' }}>
                <CreditCard size={56} color="var(--text-muted)" style={{ margin: '0 auto 20px' }} />
                <h3 className="mb-sm">No Domain Purchases</h3>
                <p className="text-muted mb-0">Paid domain purchases will appear here after merchants start checkout.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2" style={{ gap: 'var(--space-xl)' }}>
              {purchases.map(purchase => (
                <div key={purchase.id} className="card">
                  <div className="card-body">
                    <div className="flex justify-between items-start mb-lg">
                      <div>
                        <h3 className="mb-xs font-bold">{purchase.domain}</h3>
                        <p className="text-muted text-sm mb-0">
                          {purchase.store?.name || 'No store'} - {purchase.merchant?.email || 'No merchant email'}
                        </p>
                      </div>
                      {getPurchaseStatusBadge(purchase.status)}
                    </div>

                    <div className="grid grid-cols-2 gap-md mb-lg">
                      <div>
                        <div className="card-title">Amount</div>
                        <div className="text-sm font-semibold">{formatMoney(purchase.totalAmount, purchase.currency)}</div>
                      </div>
                      <div>
                        <div className="card-title">VAT</div>
                        <div className="text-sm font-semibold">
                          {formatMoney(purchase.vatAmount, purchase.currency)} ({Number(purchase.vatRate || 0)}%)
                        </div>
                      </div>
                      <div>
                        <div className="card-title">Paystack Ref</div>
                        <div className="text-sm font-semibold font-mono">
                          {purchase.paystackReference ? `${purchase.paystackReference.substring(0, 18)}...` : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="card-title">Created</div>
                        <div className="text-sm font-semibold">{formatDate(purchase.createdAt)}</div>
                      </div>
                    </div>

                    {purchase.errorMessage && (
                      <div className="card mb-lg" style={{ background: 'var(--error-bg)', borderColor: 'var(--error-color)' }}>
                        <div className="card-body flex gap-sm items-start" style={{ padding: 'var(--space-md)' }}>
                          <AlertTriangle size={16} color="var(--error-color)" />
                          <span className="text-sm" style={{ color: 'var(--error-color)' }}>
                            {purchase.errorMessage}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setSelectedPurchase(purchase);
                          setShowPurchaseDetailsModal(true);
                        }}
                      >
                        <Settings size={16} />
                        Details
                      </button>

                      {canVerifyPayment(purchase) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleVerifyDomainPurchasePayment(purchase.id)}
                          disabled={purchaseActionLoading === `verify-${purchase.id}`}
                        >
                          <RefreshCw size={16} style={purchaseActionLoading === `verify-${purchase.id}` ? { animation: 'spin 1s linear infinite' } : undefined} />
                          Verify Payment
                        </button>
                      )}

                      {canRetryProvisioning(purchase) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRetryDomainProvisioning(purchase.id)}
                          disabled={purchaseActionLoading === `retry-${purchase.id}`}
                        >
                          <RotateCcw size={16} />
                          Retry
                        </button>
                      )}

                      {purchase.customDomain?.domain && (
                        <button
                          className="btn btn-sm"
                          onClick={() => window.open(`https://${purchase.customDomain.domain}`, '_blank')}
                        >
                          <ExternalLink size={16} />
                          Open
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="mb-0">Add Custom Domain</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowAddModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Store / Merchant</label>
                <select
                  className="input"
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  required
                >
                  <option value="">Select a store</option>
                  {stores.map(store => {
                    const tier = store.owner?.merchantTier || 'FREE';
                    const canHaveCustomDomain = tier === 'PRO' || tier === 'PREMIUM';
                    return (
                      <option
                        key={store.id}
                        value={store.id}
                        disabled={!canHaveCustomDomain}
                      >
                        {store.name} - {store.owner?.username || 'Unknown'} (ID: {store.owner?.id?.substring(0, 8)}...) - {tier} {!canHaveCustomDomain ? '❌' : '✓'}
                      </option>
                    );
                  })}
                </select>
                <p className="form-help">
                  Only PRO and PREMIUM tier merchants can use custom domains
                </p>
              </div>

              <div className="form-group">
                <label>Domain Name</label>
                <input
                  type="text"
                  className="input"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  required
                />
                <p className="form-help">
                  Enter the domain name without http:// or https://
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setShowAddModal(false)}
                disabled={verifying}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddDomain}
                disabled={verifying || !newDomain.trim()}
              >
                {verifying ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Create Domain
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Domain Details Modal */}
      {selectedDomain && showDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="mb-0">Manage {selectedDomain.domain}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowDetailsModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="card mb-xl">
                <div className="card-body">
                  <h4 className="mb-md">Domain Information</h4>
                  <div className="grid grid-cols-2 gap-lg">
                    <div>
                      <div className="card-title">Domain</div>
                      <div className="text-base font-semibold">{selectedDomain.domain}</div>
                    </div>
                    <div>
                      <div className="card-title">Status</div>
                      <div>{getStatusBadge(selectedDomain.status)}</div>
                    </div>
                    <div>
                      <div className="card-title">Store</div>
                      <div className="text-base font-semibold">{selectedDomain.store?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="card-title">Merchant</div>
                      <div className="text-base font-semibold">{selectedDomain.store?.owner?.username || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="card-title">Merchant ID</div>
                      <div className="text-base font-semibold font-mono">
                        {selectedDomain.store?.owner?.id || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Tier</div>
                      <div>
                        <span className={`badge ${selectedDomain.store?.owner?.merchantTier === 'PREMIUM' ? 'badge-success' : selectedDomain.store?.owner?.merchantTier === 'PRO' ? 'badge-info' : 'badge-neutral'}`}>
                          {selectedDomain.store?.owner?.merchantTier || 'FREE'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!selectedDomain.verified && (
                <div className="card" style={{ background: 'var(--info-bg)', borderColor: 'var(--info-color)' }}>
                  <div className="card-body">
                    <h4 className="mb-md" style={{ color: 'var(--info-color)' }}>DNS Configuration Required</h4>
                    <p className="text-sm mb-lg">Please configure the following DNS records:</p>
                    <div className="card mb-0" style={{ background: 'var(--panel)' }}>
                      <div className="card-body">
                        <div className="flex justify-between items-center mb-sm">
                          <span className="badge badge-info">CNAME</span>
                          <button
                            className="btn btn-sm"
                            onClick={() => copyToClipboard(`${selectedDomain.domain}.revstar.com`)}
                          >
                            <Copy size={14} />
                            Copy
                          </button>
                        </div>
                        <div className="text-sm mb-xs">
                          <strong>Name:</strong> www (or @)
                        </div>
                        <div className="text-sm">
                          <strong>Value:</strong> {selectedDomain.domain}.revstar.com
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              {!selectedDomain.verified && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleVerifyDomain(selectedDomain.id);
                    setShowDetailsModal(false);
                  }}
                >
                  <RefreshCw size={16} />
                  Verify Domain
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Domain Purchase Details Modal */}
      {selectedPurchase && showPurchaseDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowPurchaseDetailsModal(false)}>
          <div className="modal-content" style={{ maxWidth: '760px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="mb-xs">Purchased Domain</h2>
                <p className="text-muted mb-0">{selectedPurchase.domain}</p>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowPurchaseDetailsModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="card mb-xl">
                <div className="card-body">
                  <div className="flex justify-between items-start gap-md mb-lg">
                    <div>
                      <div className="card-title">Status</div>
                      <div>{getPurchaseStatusBadge(selectedPurchase.status)}</div>
                    </div>
                    <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
                      {canVerifyPayment(selectedPurchase) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleVerifyDomainPurchasePayment(selectedPurchase.id)}
                          disabled={purchaseActionLoading === `verify-${selectedPurchase.id}`}
                        >
                          <RefreshCw size={16} style={purchaseActionLoading === `verify-${selectedPurchase.id}` ? { animation: 'spin 1s linear infinite' } : undefined} />
                          Verify Payment
                        </button>
                      )}
                      {canRetryProvisioning(selectedPurchase) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRetryDomainProvisioning(selectedPurchase.id)}
                          disabled={purchaseActionLoading === `retry-${selectedPurchase.id}`}
                        >
                          <RotateCcw size={16} />
                          Retry Provisioning
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-lg">
                    <div>
                      <div className="card-title">Store</div>
                      <div className="text-base font-semibold">{selectedPurchase.store?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="card-title">Store URL</div>
                      <div className="text-base font-semibold">{selectedPurchase.store?.slug || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="card-title">Merchant</div>
                      <div className="text-base font-semibold">{selectedPurchase.merchant?.email || selectedPurchase.merchant?.username || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="card-title">Tier</div>
                      <span className={`badge ${selectedPurchase.merchant?.merchantTier === 'PREMIUM' ? 'badge-success' : selectedPurchase.merchant?.merchantTier === 'PRO' ? 'badge-info' : 'badge-neutral'}`}>
                        {selectedPurchase.merchant?.merchantTier || 'FREE'}
                      </span>
                    </div>
                    <div>
                      <div className="card-title">Base Amount</div>
                      <div className="text-base font-semibold">{formatMoney(selectedPurchase.baseAmount, selectedPurchase.currency)}</div>
                    </div>
                    <div>
                      <div className="card-title">VAT</div>
                      <div className="text-base font-semibold">
                        {formatMoney(selectedPurchase.vatAmount, selectedPurchase.currency)} ({Number(selectedPurchase.vatRate || 0)}%)
                      </div>
                    </div>
                    <div>
                      <div className="card-title">Total Paid</div>
                      <div className="text-base font-semibold">{formatMoney(selectedPurchase.totalAmount, selectedPurchase.currency)}</div>
                    </div>
                    <div>
                      <div className="card-title">Registration Period</div>
                      <div className="text-base font-semibold">{selectedPurchase.regPeriod || 1} year</div>
                    </div>
                    <div>
                      <div className="card-title">Created</div>
                      <div className="text-base font-semibold">{formatDate(selectedPurchase.createdAt)}</div>
                    </div>
                    <div>
                      <div className="card-title">Paid</div>
                      <div className="text-base font-semibold">{formatDate(selectedPurchase.paidAt)}</div>
                    </div>
                    <div>
                      <div className="card-title">Provisioned</div>
                      <div className="text-base font-semibold">{formatDate(selectedPurchase.provisionedAt)}</div>
                    </div>
                    <div>
                      <div className="card-title">Provider Order</div>
                      <div className="text-base font-semibold font-mono">{selectedPurchase.providerOrderId || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-xl">
                <div className="card-body">
                  <h4 className="mb-md">Payment Reference</h4>
                  <div className="flex justify-between items-center gap-md" style={{ flexWrap: 'wrap' }}>
                    <code style={{ wordBreak: 'break-word' }}>{selectedPurchase.paystackReference || 'N/A'}</code>
                    {selectedPurchase.paystackReference && (
                      <button className="btn btn-sm" onClick={() => copyToClipboard(selectedPurchase.paystackReference)}>
                        <Copy size={14} />
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {selectedPurchase.customDomain && (
                <div className="card mb-xl">
                  <div className="card-body">
                    <h4 className="mb-md">Connected Custom Domain</h4>
                    <div className="grid grid-cols-2 gap-lg">
                      <div>
                        <div className="card-title">Domain</div>
                        <div className="text-base font-semibold">{selectedPurchase.customDomain.domain}</div>
                      </div>
                      <div>
                        <div className="card-title">Status</div>
                        <div>{getStatusBadge(selectedPurchase.customDomain.status)}</div>
                      </div>
                      <div>
                        <div className="card-title">Provider Status</div>
                        <div className="text-base font-semibold">{selectedPurchase.customDomain.providerStatus || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="card-title">SSL</div>
                        <span className={`badge ${selectedPurchase.customDomain.sslEnabled ? 'badge-success' : 'badge-neutral'}`}>
                          {selectedPurchase.customDomain.sslEnabled ? 'Enabled' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedPurchase.errorMessage && (
                <div className="card" style={{ background: 'var(--error-bg)', borderColor: 'var(--error-color)' }}>
                  <div className="card-body">
                    <div className="flex gap-sm items-start">
                      <AlertTriangle size={18} color="var(--error-color)" />
                      <div>
                        <h4 className="mb-sm" style={{ color: 'var(--error-color)' }}>Provisioning Error</h4>
                        <p className="text-sm mb-0" style={{ color: 'var(--error-color)' }}>{selectedPurchase.errorMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowPurchaseDetailsModal(false)}>
                Close
              </button>
              {selectedPurchase.customDomain?.domain && (
                <button
                  className="btn btn-primary"
                  onClick={() => window.open(`https://${selectedPurchase.customDomain.domain}`, '_blank')}
                >
                  <ExternalLink size={16} />
                  Open Domain
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDomains;
